import { useMemo } from 'react';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import { Layer, LayerProps, Source } from 'shared/map-exports';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import destination from '@turf/destination';

import { MapClickGetPois, MapIcon } from 'features/mapping/mapping.types';
import {
  useExternalGeoEvent,
  useExternalGeoEvents,
} from 'hooks/useExternalGeoEvents';
import { useSelectedExternalGeoEventId } from 'features/geoEvents/hooks/useSelectedExternalGeoEventId';
import { AlertWestAiHitExternalGeoEvent, ExternalGeoEvent } from 'shared/types';
import {
  ActiveExternalGeoEventMarker,
  ADEQActiveExternalGeoEventMarker,
  ADEQInactiveExternalGeoEventMarker,
  AircraftActiveExternalGeoEventMarker,
  AircraftInactiveExternalGeoEventMarker,
  AlertWestActiveExternalGeoEventMarker,
  AlertWestInactiveExternalGeoEventMarker,
  ARForestryActiveExternalGeoEventMarker,
  ARForestryInactiveExternalGeoEventMarker,
  CalFireActiveExternalGeoEventMarker,
  CalFireInactiveExternalGeoEventMarker,
  CHPActiveExternalGeoEventMarker,
  CHPInactiveExternalGeoEventMarker,
  FireGuardActiveExternalGeoEventMarker,
  FireGuardInactiveExternalGeoEventMarker,
  FireResponseActiveExternalGeoEventMarker,
  FireResponseInactiveExternalGeoEventMarker,
  FloridaForestServiceActiveExternalGeoEventMarker,
  FloridaForestServiceInactiveExternalGeoEventMarker,
  MfcActiveExternalGeoEventMarker,
  MfcInactiveExternalGeoEventMarker,
  InactiveExternalGeoEventMarker,
  N5ActiveExternalGeoEventMarker,
  N5InactiveExternalGeoEventMarker,
  NGFSActiveExternalGeoEventMarker,
  NGFSInactiveExternalGeoEventMarker,
  NIFCActiveExternalGeoEventMarker,
  NIFCInactiveExternalGeoEventMarker,
  NWSActiveExternalGeoEventMarker,
  NWSInactiveExternalGeoEventMarker,
  ODFActiveExternalGeoEventMarker,
  ODFInactiveExternalGeoEventMarker,
  PFIRSActiveExternalGeoEventMarker,
  PFIRSInactiveExternalGeoEventMarker,
  PrecipitationActiveExternalGeoEventMarker,
  PrecipitationInactiveExternalGeoEventMarker,
  PulsePointActiveExternalGeoEventMarker,
  PulsePointInactiveExternalGeoEventMarker,
  SelectedExternalGeoEventMarker,
  SpotWeatherActiveExternalGeoEventMarker,
  SpotWeatherInactiveExternalGeoEventMarker,
  TexasAMActiveExternalGeoEventMarker,
  TexasAMInactiveExternalGeoEventMarker,
  WADNRActiveExternalGeoEventMarker,
  WADNRInactiveExternalGeoEventMarker,
  WEAActiveExternalGeoEventMarker,
  WIDNRActiveExternalGeoEventMarker,
  WIDNRInactiveExternalGeoEventMarker,
  WEAInactiveExternalGeoEventMarker,
  WildCADActiveExternalGeoEventMarker,
  WildCADInactiveExternalGeoEventMarker,
} from 'features/mapping/icons';
import { addVisible } from 'shared/addVisible';
import { ExternalGeoNameMap, ExternalSource } from '../../../../../constants';

export const EXTERNAL_GEO_EVENT_ICONS_LAYER_ID = 'external-geo-event-icons';
const LINES_LAYER_ID = 'external-geo-events-lines';
const POINTS_SOURCE_ID = 'external-geo-event-source-points';
const LINES_SOURCE_ID = 'external-geo-event-source-lines';
const SELECTED_POINTS_SOURCE_ID = 'external-geo-event-source-points-selected';
const SELECTED_ICONS_LAYER_ID = 'external-geo-event-icons-selected';

const CAM_AZIMUTH_LINE_LENGTH_MI = 30;
const DEFAULT_MIN_CUTOFF = 30;
const ALERT_WEST_CUTOFF = 15;

const MinutesCutoffMap: Record<ExternalSource, number> = {
  pulsepoint: DEFAULT_MIN_CUTOFF,
  wildcad: DEFAULT_MIN_CUTOFF,
  nifc: DEFAULT_MIN_CUTOFF,
  chp: DEFAULT_MIN_CUTOFF,
  aircraft_detection: DEFAULT_MIN_CUTOFF,
  alert_west: ALERT_WEST_CUTOFF,
  adeq: DEFAULT_MIN_CUTOFF,
  spot_forecast: DEFAULT_MIN_CUTOFF,
  pfirs: DEFAULT_MIN_CUTOFF,
  alert_west_detection: ALERT_WEST_CUTOFF,
  wea: DEFAULT_MIN_CUTOFF,
  wea_pbs_warn: DEFAULT_MIN_CUTOFF,
  cal_fire: DEFAULT_MIN_CUTOFF,
  n5_shield: DEFAULT_MIN_CUTOFF,
  ngfs: DEFAULT_MIN_CUTOFF,
  odf: DEFAULT_MIN_CUTOFF,
  dnr: DEFAULT_MIN_CUTOFF,
  fire_guard: DEFAULT_MIN_CUTOFF,
  texas_am: DEFAULT_MIN_CUTOFF,
  firesponse_nc: DEFAULT_MIN_CUTOFF,
  firesponse_ga: DEFAULT_MIN_CUTOFF,
  firesponse_fl: DEFAULT_MIN_CUTOFF,
  firesponse_ky: DEFAULT_MIN_CUTOFF,
  firesponse_sc: DEFAULT_MIN_CUTOFF,
  firesponse_tn: DEFAULT_MIN_CUTOFF,
  firesponse_va: DEFAULT_MIN_CUTOFF,
  florida_forest_service: DEFAULT_MIN_CUTOFF,
  mfc: DEFAULT_MIN_CUTOFF,
  wi_dnr: DEFAULT_MIN_CUTOFF,
  nws_alert: DEFAULT_MIN_CUTOFF,
  nws_lsr: DEFAULT_MIN_CUTOFF,
  synoptic_precipitation: DEFAULT_MIN_CUTOFF,
  noaa_river_gauge: DEFAULT_MIN_CUTOFF,
  arkansas_forestry: DEFAULT_MIN_CUTOFF,
};

const InactiveIconsMap: Record<ExternalSource, MapIcon> = {
  pulsepoint: PulsePointInactiveExternalGeoEventMarker,
  wildcad: WildCADInactiveExternalGeoEventMarker,
  nifc: NIFCInactiveExternalGeoEventMarker,
  chp: CHPInactiveExternalGeoEventMarker,
  aircraft_detection: AircraftInactiveExternalGeoEventMarker,
  alert_west: AlertWestInactiveExternalGeoEventMarker,
  adeq: ADEQInactiveExternalGeoEventMarker,
  spot_forecast: SpotWeatherInactiveExternalGeoEventMarker,
  pfirs: PFIRSInactiveExternalGeoEventMarker,
  alert_west_detection: AlertWestInactiveExternalGeoEventMarker,
  wea: WEAInactiveExternalGeoEventMarker,
  wea_pbs_warn: WEAInactiveExternalGeoEventMarker,
  cal_fire: CalFireInactiveExternalGeoEventMarker,
  n5_shield: N5InactiveExternalGeoEventMarker,
  ngfs: NGFSInactiveExternalGeoEventMarker,
  odf: ODFInactiveExternalGeoEventMarker,
  dnr: WADNRInactiveExternalGeoEventMarker,
  fire_guard: FireGuardInactiveExternalGeoEventMarker,
  texas_am: TexasAMInactiveExternalGeoEventMarker,
  firesponse_nc: FireResponseInactiveExternalGeoEventMarker,
  firesponse_ga: FireResponseInactiveExternalGeoEventMarker,
  firesponse_fl: FireResponseInactiveExternalGeoEventMarker,
  firesponse_ky: FireResponseInactiveExternalGeoEventMarker,
  firesponse_sc: FireResponseInactiveExternalGeoEventMarker,
  firesponse_tn: FireResponseInactiveExternalGeoEventMarker,
  firesponse_va: FireResponseInactiveExternalGeoEventMarker,
  florida_forest_service: FloridaForestServiceInactiveExternalGeoEventMarker,
  mfc: MfcInactiveExternalGeoEventMarker,
  wi_dnr: WIDNRInactiveExternalGeoEventMarker,
  nws_alert: NWSInactiveExternalGeoEventMarker,
  nws_lsr: NWSInactiveExternalGeoEventMarker,
  synoptic_precipitation: PrecipitationInactiveExternalGeoEventMarker,
  noaa_river_gauge: SpotWeatherInactiveExternalGeoEventMarker,
  arkansas_forestry: ARForestryInactiveExternalGeoEventMarker,
};

const ActiveIconsMap: Record<ExternalSource, MapIcon> = {
  pulsepoint: PulsePointActiveExternalGeoEventMarker,
  wildcad: WildCADActiveExternalGeoEventMarker,
  nifc: NIFCActiveExternalGeoEventMarker,
  chp: CHPActiveExternalGeoEventMarker,
  aircraft_detection: AircraftActiveExternalGeoEventMarker,
  alert_west: AlertWestActiveExternalGeoEventMarker,
  adeq: ADEQActiveExternalGeoEventMarker,
  spot_forecast: SpotWeatherActiveExternalGeoEventMarker,
  pfirs: PFIRSActiveExternalGeoEventMarker,
  alert_west_detection: AlertWestActiveExternalGeoEventMarker,
  wea: WEAActiveExternalGeoEventMarker,
  wea_pbs_warn: WEAActiveExternalGeoEventMarker,
  cal_fire: CalFireActiveExternalGeoEventMarker,
  n5_shield: N5ActiveExternalGeoEventMarker,
  ngfs: NGFSActiveExternalGeoEventMarker,
  odf: ODFActiveExternalGeoEventMarker,
  dnr: WADNRActiveExternalGeoEventMarker,
  fire_guard: FireGuardActiveExternalGeoEventMarker,
  texas_am: TexasAMActiveExternalGeoEventMarker,
  firesponse_nc: FireResponseActiveExternalGeoEventMarker,
  firesponse_ga: FireResponseActiveExternalGeoEventMarker,
  firesponse_fl: FireResponseActiveExternalGeoEventMarker,
  firesponse_ky: FireResponseActiveExternalGeoEventMarker,
  firesponse_sc: FireResponseActiveExternalGeoEventMarker,
  firesponse_tn: FireResponseActiveExternalGeoEventMarker,
  firesponse_va: FireResponseActiveExternalGeoEventMarker,
  florida_forest_service: FloridaForestServiceActiveExternalGeoEventMarker,
  mfc: MfcActiveExternalGeoEventMarker,
  wi_dnr: WIDNRActiveExternalGeoEventMarker,
  nws_alert: NWSActiveExternalGeoEventMarker,
  nws_lsr: NWSActiveExternalGeoEventMarker,
  synoptic_precipitation: PrecipitationActiveExternalGeoEventMarker,
  noaa_river_gauge: SpotWeatherActiveExternalGeoEventMarker,
  arkansas_forestry: ARForestryActiveExternalGeoEventMarker,
};

type ExternalGeoEventsLayerProps = {
  visible: boolean;
  interactive?: boolean;
};

export type ExternalGeoEventFeatureProperties = {
  id: number;
  name: string;
  icon: string;
  scale: number;
  offsetX: number;
  offsetY: number;
  anchor: 'bottom' | 'center';
  isPrescribed: boolean;
  opacity: number;
  acreage?: number;
  address?: string;
  containment?: number;
  dateStart?: string;
  externalSource: string;
  messageId: string;
  channel: string;
  diffInMinutesStr: string;
  sourceName: string;
  index: number;
  indexInv: number;
};

type LineFeatureProperties = {
  opacity: number;
  strokeWidth: number;
};

export const externalGeoEventIconStyle: LayerProps = {
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-allow-overlap': true,
    'icon-anchor': ['get', 'anchor'],
    'icon-ignore-placement': false,
    'icon-size': ['get', 'scale'],
    'icon-offset': [
      ['get', 'offsetX'],
      ['get', 'offsetY'],
    ],
    'symbol-sort-key': ['get', 'index'],
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
};

const lineStyle: LayerProps = {
  type: 'line',
  paint: {
    'line-opacity': ['get', 'opacity'],
    'line-width': ['get', 'strokeWidth'],
    'line-color': '#FF55FF',
  },
};

const getDiffInMinutes = (record: ExternalGeoEvent): number => {
  const { dateCreated: dateCreatedStr } = record;
  const now = new Date();
  const dateCreated = new Date(dateCreatedStr);
  return differenceInMinutes(now, dateCreated);
};

const getOpacity = (record: ExternalGeoEvent): number => {
  const diffInMinutes = getDiffInMinutes(record);

  const cutoff =
    MinutesCutoffMap[record.data.externalSource] || DEFAULT_MIN_CUTOFF;
  if (diffInMinutes >= cutoff) {
    return 0;
  }
  return 1 - diffInMinutes / cutoff;
};

const getScale = (record: ExternalGeoEvent): number => {
  const diffInMinutes = getDiffInMinutes(record);
  const baseScale = 0.9;

  const cutoff =
    MinutesCutoffMap[record.data.externalSource] || DEFAULT_MIN_CUTOFF;
  if (diffInMinutes >= cutoff) {
    return 0;
  }
  const sizeFactor = 1 - (diffInMinutes / cutoff) ** 4;
  return baseScale * sizeFactor;
};

const hasBeenAcked = (record: ExternalGeoEvent): boolean => {
  return record.requestedAcks?.some((ack) => ack.acks.length > 0) ?? false;
};

const getIconForEvent = (
  externalSource: ExternalSource,
  useInactiveIcon: boolean,
): MapIcon =>
  useInactiveIcon
    ? InactiveIconsMap[externalSource] || InactiveExternalGeoEventMarker
    : ActiveIconsMap[externalSource] || ActiveExternalGeoEventMarker;

export const toExternalGeoEventPointFeature = (
  record: ExternalGeoEvent,
  options: { forcedState?: 'active' | 'inactive'; selected?: boolean } = {},
): Feature<Geometry, ExternalGeoEventFeatureProperties> => {
  const { forcedState, selected } = options;
  const forceInactive = forcedState === 'inactive';
  const forceFullVisibility = Boolean(forcedState !== undefined || selected);
  const {
    id,
    lat,
    lng,
    messageId,
    channel,
    data: {
      acreage,
      isPrescribed,
      containment,
      name,
      dateStart,
      address,
      externalSource,
    },
  } = record;

  let sourceName = ExternalGeoNameMap[externalSource] || externalSource;
  const acked = hasBeenAcked(record);
  if (acked && !forceInactive) {
    sourceName = `${sourceName} - ACKed`;
  }
  const useInactiveIcon = acked || forceInactive;
  const icon = selected
    ? SelectedExternalGeoEventMarker
    : getIconForEvent(externalSource, useInactiveIcon);
  const opacity = forceFullVisibility ? 1 : getOpacity(record);
  const scale =
    (forceFullVisibility ? 0.9 : getScale(record)) * (icon.scale || 1.0);
  const offsetX = icon.offset?.[0] || 0;
  const offsetY = icon.offset?.[1] || 0;
  const anchor = icon.offset ? 'bottom' : 'center';

  const diffInMinutesStr = `${getDiffInMinutes(record)} mins ago`;

  // Always place alert_west events on the bottom since they can be noisy.
  const index =
    externalSource === 'alert_west' ? 0 : Date.parse(record.dateCreated);
  const indexInv = 1 / (index + 0.1);

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
    properties: {
      id,
      name,
      icon: icon.name,
      diffInMinutesStr,
      sourceName,
      scale,
      isPrescribed,
      opacity,
      offsetX,
      offsetY,
      anchor,
      acreage,
      address,
      containment,
      dateStart,
      externalSource,
      messageId,
      channel,
      index,
      indexInv,
    },
  };
};

const toLineGeojsonFeature = (
  record: ExternalGeoEvent,
): Feature<Geometry, LineFeatureProperties> | null => {
  const {
    data: { externalSource },
  } = record;
  if (externalSource !== 'alert_west') {
    return null;
  }

  const {
    lat,
    lng,
    data: { hitAz, hitConfidence },
  } = record as AlertWestAiHitExternalGeoEvent;
  const opacity = getOpacity(record);
  const endPoint = destination([lng, lat], CAM_AZIMUTH_LINE_LENGTH_MI, hitAz, {
    units: 'miles',
  });
  const strokeWidth = Math.max(1, (hitConfidence - 0.5) * 10);
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [[lng, lat], endPoint.geometry.coordinates],
    },
    properties: {
      opacity,
      strokeWidth,
    },
  };
};

export const externalGeoEventsClickHandler: MapClickGetPois<
  ExternalGeoEventFeatureProperties
> = (geoJsonFeatures, _, externalGeoEvents) => {
  const features = [...geoJsonFeatures];
  const feature = features[0];
  const match = externalGeoEvents.find(
    (extGeo) => extGeo.id === feature.properties.id,
  );
  if (!match) {
    return [];
  }
  return [
    {
      type: 'externalGeoEvent',
      metadata: match,
    },
  ];
};

export const ExternalGeoEventsLayer = (
  props: ExternalGeoEventsLayerProps,
): JSX.Element => {
  const { visible, interactive = true } = props;
  const { externalGeoEvents } = useExternalGeoEvents();

  const selectedId = useSelectedExternalGeoEventId();
  const { externalGeoEvent: selectedEvent } = useExternalGeoEvent(selectedId);

  const selectedInRecentList = useMemo(
    () =>
      Boolean(selectedId) &&
      externalGeoEvents.some((e) => String(e.id) === selectedId),
    [externalGeoEvents, selectedId],
  );

  // The selected pin only needs its own source when the main layer won't render the event
  const needsSelectedPin =
    Boolean(selectedEvent) && (!visible || !selectedInRecentList);

  const geojsonPoints: FeatureCollection<
    Geometry,
    ExternalGeoEventFeatureProperties
  > = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: externalGeoEvents.map((externalGeoEvent) =>
        toExternalGeoEventPointFeature(externalGeoEvent, {
          selected: String(externalGeoEvent.id) === selectedId,
        }),
      ),
    }),
    [externalGeoEvents, selectedId],
  );

  const geojsonLines: FeatureCollection<Geometry, LineFeatureProperties> =
    useMemo(
      () => ({
        type: 'FeatureCollection',
        features: externalGeoEvents
          .map(toLineGeojsonFeature)
          .filter(Boolean) as Feature<Geometry, LineFeatureProperties>[],
      }),
      [externalGeoEvents],
    );

  const geojsonSelected: FeatureCollection<
    Geometry,
    ExternalGeoEventFeatureProperties
  > = useMemo(
    () => ({
      type: 'FeatureCollection',
      features:
        needsSelectedPin && selectedEvent
          ? [
              toExternalGeoEventPointFeature(selectedEvent, {
                forcedState: selectedInRecentList ? 'active' : 'inactive',
                selected: true,
              }),
            ]
          : [],
    }),
    [needsSelectedPin, selectedEvent, selectedInRecentList],
  );

  return (
    <>
      <Source id={LINES_SOURCE_ID} type="geojson" data={geojsonLines}>
        <Layer
          id={LINES_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...addVisible(lineStyle, visible)}
        />
      </Source>
      <Source id={POINTS_SOURCE_ID} type="geojson" data={geojsonPoints}>
        <Layer
          id={EXTERNAL_GEO_EVENT_ICONS_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          metadata={{ interactive }}
          {...addVisible(externalGeoEventIconStyle, visible)}
        />
      </Source>
      <Source
        id={SELECTED_POINTS_SOURCE_ID}
        type="geojson"
        data={geojsonSelected}
      >
        <Layer
          id={SELECTED_ICONS_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...externalGeoEventIconStyle}
        />
      </Source>
    </>
  );
};
